/**
 * Compress and resize an image for vCard compatibility
 * Target: 256x256 px, < 32KB JPEG for maximum device compatibility
 */

const MAX_DIMENSION = 256;
const TARGET_SIZE_KB = 30;
const JPEG_QUALITY_START = 0.9;
const JPEG_QUALITY_MIN = 0.5;

/**
 * Compress an image file to be vCard-compatible
 * @param file - Original image file
 * @returns Compressed image as Blob with JPEG mime type
 */
export async function compressImageForVCard(file: File): Promise<Blob> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not create canvas context');
  }

  // Calculate new dimensions (maintain aspect ratio, max 256px)
  let { width, height } = img;
  if (width > height) {
    if (width > MAX_DIMENSION) {
      height = Math.round((height * MAX_DIMENSION) / width);
      width = MAX_DIMENSION;
    }
  } else {
    if (height > MAX_DIMENSION) {
      width = Math.round((width * MAX_DIMENSION) / height);
      height = MAX_DIMENSION;
    }
  }

  canvas.width = width;
  canvas.height = height;

  // Draw with white background (in case of transparent PNG)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  // Compress to JPEG, iteratively reduce quality until < 30KB
  let quality = JPEG_QUALITY_START;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);

  while (blob.size > TARGET_SIZE_KB * 1024 && quality > JPEG_QUALITY_MIN) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  return blob;
}

/**
 * Crop an image at a specific position within a circular/square crop area
 * @param file - Original image file
 * @param position - Offset from center { x, y } in pixels (relative to display size)
 * @param displaySize - The size of the crop preview in the UI
 * @param outputSize - The final output dimension (default: 256 for vCard)
 * @returns Cropped image as Blob
 */
export async function cropImageAtPosition(
  file: File,
  position: { x: number; y: number },
  displaySize: number,
  outputSize: number = MAX_DIMENSION
): Promise<Blob> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not create canvas context');
  }

  canvas.width = outputSize;
  canvas.height = outputSize;

  // Calculate the scale factor between original image and display size
  // The image is scaled to cover the crop area (like object-fit: cover)
  const scale = Math.max(displaySize / img.width, displaySize / img.height);

  // Calculate the crop region in the original image coordinates
  // Position (0,0) means centered crop, position.x/y are offsets from center
  const centerX = img.width / 2;
  const centerY = img.height / 2;

  // Convert display position offset to source image coordinates
  const offsetX = position.x / scale;
  const offsetY = position.y / scale;

  // Calculate source rectangle (what part of original image to crop)
  const srcCropSize = displaySize / scale;
  const srcX = centerX - srcCropSize / 2 - offsetX;
  const srcY = centerY - srcCropSize / 2 - offsetY;

  // Draw with white background (in case of transparent PNG)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, outputSize, outputSize);

  // Draw the cropped portion to the canvas
  ctx.drawImage(
    img,
    srcX, srcY, srcCropSize, srcCropSize, // source rect
    0, 0, outputSize, outputSize // dest rect
  );

  // Clean up object URL
  URL.revokeObjectURL(img.src);

  return canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY_START);
}

/**
 * Load an image file into an HTMLImageElement
 */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Convert canvas to blob with specified format and quality
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      type,
      quality
    );
  });
}
